import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import ScriptureCard from "@/components/ScriptureCard";
import CravingJournal from "@/components/CravingJournal";
import { BreakChainButton } from "@/components/BreakChain";
import { SUBSTANCES, MILESTONES } from "@/data/content";
import {
  Compass, LifeBuoy, HeartPulse, CheckCircle2, Circle, ArrowRight, Plus,
  Brain, Hand, Sparkles, Flag, Target, Repeat, Leaf, Route, Trophy, Check, RotateCcw,
  Flame, RefreshCw, Loader2, BookOpenText, Eye, Shield, HandHeart, ChevronDown,
} from "lucide-react";

const PATH = [
  { icon: Brain, tag: "Understand", desc: "See what your brain is doing in a craving — the trigger, the loop, the pull." },
  { icon: Hand, tag: "Interrupt", desc: "Break the pattern in the moment with a practical alternative before the urge decides for you." },
  { icon: Sparkles, tag: "Renew", desc: "Choose the next step and invite God in — every chain break lays down a new response." },
];

const STAGES = [
  {
    key: "see",
    eyebrow: "Stage 1 · Awareness & surrender",
    title: "See it & surrender it",
    icon: Eye,
    why: "You can't break what you won't name. Notice the trigger — and the reward the craving is really promising — then hand the fight over. \u201cWe take captive every thought to make it obedient to Christ.\u201d (2 Corinthians 10:5)",
  },
  {
    key: "renew",
    eyebrow: "Stage 2 · Renew your mind",
    title: "Renew your mind",
    icon: Sparkles,
    why: "Change begins with who you believe you are. In Christ the old is gone and the new has come. \u201cBe transformed by the renewing of your mind.\u201d (Romans 12:2)",
  },
  {
    key: "rhythms",
    eyebrow: "Stage 3 · Build new rhythms",
    title: "Build new rhythms",
    icon: Leaf,
    why: "A swept-but-empty house invites the old back (Matthew 12:43\u201345). Don't only remove nicotine \u2014 fill the space. Keep the moment, swap the response. \u201cPut on the new self.\u201d (Ephesians 4:24)",
  },
  {
    key: "stand",
    eyebrow: "Stage 4 · Stand firm",
    title: "Stand firm when it's hard",
    icon: Shield,
    why: "Cravings still come \u2014 and when they do, there's always a way through. \u201cGod is faithful; he will also provide a way of escape.\u201d (1 Corinthians 10:13)",
  },
  {
    key: "walk",
    eyebrow: "Stage 5 · Grace & community",
    title: "Walk it out together",
    icon: HandHeart,
    why: "Freedom is walked out in grace, not shame \u2014 and never alone. \u201cConfess to one another and pray for one another, that you may be healed.\u201d (James 5:16) His mercies are new every morning.",
  },
];

function RoadmapCard({ to, icon: Icon, eyebrow, title, desc, testid, accent = "teal" }) {
  const ring = accent === "gold" ? "border-brand-gold/40" : "border-brand-teal/30";
  const iconColor = accent === "gold" ? "text-brand-gold" : "text-brand-teal-dark";
  return (
    <Link to={to} data-testid={testid}
      className={`group flex items-start gap-4 rounded-3xl bg-card border ${ring} p-6 shadow-soft transition-all duration-200 hover:-translate-y-0.5`}>
      <div className="rounded-2xl bg-sage w-12 h-12 flex items-center justify-center shrink-0">
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-1">{eyebrow}</p>
        <h3 className="font-serif text-2xl text-olive">{title}</h3>
        <p className="text-sm text-olive/70 mt-1.5 leading-relaxed">{desc}</p>
      </div>
      <ArrowRight className="w-5 h-5 text-stone mt-1 shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
    </Link>
  );
}

function BuildStrategyPrompt({ testid, line }) {
  return (
    <div className="rounded-3xl border border-dashed border-border p-7 text-center" data-testid={testid}>
      <p className="text-olive/70 max-w-md mx-auto mb-4">{line}</p>
      <Link to="/app/strategy" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sand text-sm font-medium transition-colors hover:bg-terracotta-soft">
        <Plus className="w-4 h-4" /> Build my strategy
      </Link>
    </div>
  );
}

function TriggerCard({ t, i, count, streak, regenIdx, onWin, onUndo, onRegen }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
      className="rounded-3xl bg-card border border-border p-6 shadow-soft flex flex-col" data-testid={`journey-trigger-${i}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <p className="font-serif text-2xl text-olive">{t.trigger}</p>
        {count > 0 && (
          <span data-testid={`trigger-win-count-${i}`} className="inline-flex items-center gap-1 rounded-full bg-gold/15 text-gold px-2.5 py-1 text-sm font-semibold shrink-0">
            <Trophy className="w-3.5 h-3.5" /> {count}
          </span>
        )}
      </div>
      {t.why && <p className="text-sm text-stone mb-4">{t.why}</p>}
      {t.swap && (
        <div className="flex items-start gap-2 rounded-2xl bg-sage p-3 mb-2">
          <Repeat className="w-4 h-4 text-sky mt-0.5 shrink-0" />
          <p className="text-sm text-olive"><span className="font-medium">Swap:</span> {t.swap}</p>
        </div>
      )}
      {t.new_habit && (
        <div className="flex items-start gap-2 rounded-2xl bg-gold/10 p-3">
          <Leaf className="w-4 h-4 text-gold mt-0.5 shrink-0" />
          <p className="text-sm text-olive"><span className="font-medium">Build:</span> {t.new_habit}</p>
        </div>
      )}
      <button data-testid={`trigger-regen-btn-${i}`} onClick={onRegen} disabled={regenIdx !== null}
        className="self-start inline-flex items-center gap-1.5 text-xs text-terracotta hover:underline disabled:opacity-60 mt-3">
        {regenIdx === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        {regenIdx === i ? "Finding a fresh idea…" : "Try a different idea"}
      </button>
      {streak >= 2 && (
        <p data-testid={`trigger-streak-${i}`} className="flex items-center gap-1.5 text-xs text-terracotta font-semibold mt-3">
          <Flame className="w-3.5 h-3.5" /> {streak} days running — keep it going
        </p>
      )}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
        <button data-testid={`trigger-win-btn-${i}`} onClick={onWin}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-sand py-2.5 text-sm font-medium transition-colors duration-200 hover:bg-terracotta-soft">
          <Check className="w-4 h-4" /> I chose the new habit
        </button>
        {count > 0 && (
          <button data-testid={`trigger-win-undo-${i}`} onClick={onUndo} title="Undo last win"
            className="rounded-full p-2.5 text-stone border border-border hover:bg-sage transition-colors">
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function JourneyPage() {
  const [data, setData] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [wins, setWins] = useState({ counts: {}, total: 0, week: 0, streaks: {} });
  const [regenIdx, setRegenIdx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    Promise.all([client.get("/dashboard"), client.get("/strategy"), client.get("/habits/wins")])
      .then(([d, s, w]) => {
        setData(d.data);
        setStrategy(s.data && s.data.id ? s.data : null);
        setWins(w.data || { counts: {}, total: 0, week: 0, streaks: {} });
      })
      .finally(() => setLoading(false));
  }, []);

  const regenTrigger = async (i) => {
    if (!strategy?.id || regenIdx !== null) return;
    setRegenIdx(i);
    try {
      const r = await client.post(`/strategy/${strategy.id}/regenerate-triggers`, { index: i });
      setStrategy(r.data.strategy);
      toast.success("A fresh idea for that trigger.");
    } catch (e) {
      toast.error("Couldn't refresh that idea. Please try again.");
    } finally {
      setRegenIdx(null);
    }
  };

  const winKey = (t, i) =>
    (String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60)) || `trigger-${i}`;

  const markWin = async (key, label) => {
    setWins((w) => ({
      ...w,
      counts: { ...w.counts, [key]: (w.counts[key] || 0) + 1 },
      total: w.total + 1,
      week: w.week + 1,
    }));
    toast.success("Win logged — a chain broken, a habit built.");
    try { await client.post("/habits/win", { key, label }); } catch (e) { /* keep optimistic */ }
  };

  const undoWin = async (key) => {
    setWins((w) => {
      const c = Math.max(0, (w.counts[key] || 0) - 1);
      return { ...w, counts: { ...w.counts, [key]: c }, total: Math.max(0, w.total - 1), week: Math.max(0, w.week - 1) };
    });
    try { await client.delete("/habits/win", { params: { key } }); } catch (e) { /* keep optimistic */ }
  };

  if (loading) return <div className="animate-pulse text-stone" data-testid="journey-loading">Opening your journey…</div>;

  const active = data?.active_plan;
  const sub = active ? SUBSTANCES[active.substance] : null;
  const milestones = active ? (MILESTONES[active.substance] || []) : [];
  const s = strategy?.strategy;
  const triggers = s?.triggers || [];
  const habits = s?.habits || [];

  // ---------- Before setup ----------
  if (!active) {
    return (
      <div className="space-y-8" data-testid="journey-page">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs text-brand-teal-dark mb-2">The road to freedom</p>
          <h1 className="font-serif text-display text-olive">My Journey</h1>
          <p className="text-olive/70 mt-3 max-w-2xl">
            Freedom isn't found in trying harder. It's His strength, your faith, and small, intentional steps —
            worked out over time. Here's the path we'll walk together.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4" data-testid="journey-path">
          {PATH.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.tag} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                className="rounded-3xl bg-card border border-border p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-bold text-stone">0{i + 1}</span>
                  <div className="rounded-xl bg-sage w-10 h-10 flex items-center justify-center"><Icon className="w-5 h-5 text-brand-teal-dark" /></div>
                </div>
                <h3 className="font-serif text-2xl text-olive mb-1">{p.tag}</h3>
                <p className="text-sm text-olive/70 leading-relaxed">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="rounded-3xl bg-brand-navy text-white p-8 sm:p-10 text-center" data-testid="journey-start-cta">
          <div className="rounded-2xl bg-white/10 w-14 h-14 flex items-center justify-center mx-auto mb-5"><Compass className="w-6 h-6 text-brand-teal" /></div>
          <h2 className="font-serif text-3xl text-white mb-2">Start my journey</h2>
          <p className="text-white/70 max-w-md mx-auto mb-6">
            Choose what you're overcoming, set your quit day, and name your reasons. We'll build a gentle plan from there.
          </p>
          <Link to="/app/plan" data-testid="journey-build-plan"
            className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-8 py-4 text-brand-navy font-semibold transition-transform duration-200 hover:scale-[1.02]">
            <Plus className="w-4 h-4" /> Build my plan
          </Link>
        </div>
      </div>
    );
  }

  // ---------- After setup: guided formation path ----------
  const daysClean = data.days_clean ?? 0;
  const doneMap = {
    0: triggers.length > 0,
    1: !!active.reason,
    2: wins.total > 0,
    3: (data.chain_breaks ?? 0) > 0,
    4: daysClean >= 7 || wins.total >= 5,
  };
  const currentIndex = [0, 1, 2, 3, 4].find((i) => !doneMap[i]);
  const activeStage = currentIndex === undefined ? 4 : currentIndex;
  const shownOpen = openIndex === null ? activeStage : openIndex;

  const renderStageContent = (i) => {
    if (i === 0) {
      return (
        <div className="space-y-4">
          {triggers.length > 0 ? (
            <div className="rounded-2xl bg-sage/60 p-5">
              <p className="text-sm text-olive/70 mb-3">What you've named so far:</p>
              <div className="flex flex-wrap gap-2">
                {triggers.map((t, idx) => (
                  <span key={idx} className="rounded-full bg-white border border-border px-3 py-1.5 text-sm text-olive">{t.trigger}</span>
                ))}
              </div>
              <Link to="/app/strategy" className="mt-4 inline-flex items-center gap-1.5 text-sm text-terracotta hover:underline">
                Refine what trips you up <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <BuildStrategyPrompt testid="journey-see-empty" line="Name what trips you up and we'll plan a swap for each — so the old pattern loses its grip." />
          )}
          <CravingJournal />
        </div>
      );
    }
    if (i === 1) {
      return (
        <div className="space-y-4">
          {active.reason ? (
            <div className="rounded-2xl bg-gold/10 border border-gold/20 p-5" data-testid="journey-your-why">
              <p className="text-[11px] uppercase tracking-[0.15em] text-gold mb-1.5">Your why</p>
              <p className="text-olive leading-relaxed">{active.reason}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-5 text-center">
              <p className="text-olive/70 mb-3">Name the reasons behind your freedom — they'll anchor you when it's hard.</p>
              <Link to="/app/plan" className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:underline">
                Add your reasons <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
          <div className="rounded-2xl bg-card border border-border p-5">
            <p className="text-olive/80 leading-relaxed mb-3">You are not the person you were. In Christ, the old is gone — you're becoming someone who is free. Let truth, not the craving, have the last word.</p>
            <Link to="/app/verses" data-testid="journey-verses" className="inline-flex items-center gap-1.5 text-sm text-brand-teal-dark font-medium hover:underline">
              <BookOpenText className="w-4 h-4" /> Find your anchor verse
            </Link>
          </div>
        </div>
      );
    }
    if (i === 2) {
      return (
        <div className="space-y-6">
          <div data-testid="journey-triggers">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-terracotta" />
                <h3 className="font-serif text-h3 text-olive">Breaking your triggers</h3>
              </div>
              {triggers.length > 0 && (
                <div className="flex items-center gap-3">
                  <span data-testid="wins-summary" className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-gold px-3 py-1.5 text-sm font-semibold">
                    <Trophy className="w-4 h-4" /> {wins.total} win{wins.total === 1 ? "" : "s"}{wins.week > 0 ? ` · ${wins.week} this week` : ""}
                  </span>
                  <Link to="/app/strategy" className="text-sm text-terracotta hover:underline flex items-center gap-1">Edit <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
              )}
            </div>
            <p className="text-olive/70 mb-5 max-w-2xl text-sm">
              Keep the cue, swap the routine. Each time you choose the new habit instead of the old trigger, mark a win — small wins, stacked, become freedom.
            </p>
            {triggers.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {triggers.map((t, idx) => {
                  const key = winKey(t.trigger, idx);
                  return (
                    <TriggerCard key={idx} t={t} i={idx} count={wins.counts[key] || 0} streak={wins.streaks?.[key] || 0}
                      regenIdx={regenIdx} onWin={() => markWin(key, t.trigger)} onUndo={() => undoWin(key)} onRegen={() => regenTrigger(idx)} />
                  );
                })}
              </div>
            ) : (
              <BuildStrategyPrompt testid="journey-triggers-empty" line="Name what trips you up and we'll plan a swap for each — so the old pattern loses its grip." />
            )}
          </div>

          <div data-testid="journey-habits">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-terracotta" />
              <h3 className="font-serif text-h3 text-olive">New habits to build</h3>
            </div>
            <p className="text-olive/70 mb-5 max-w-2xl text-sm">What you're growing into as the old habits fall away — this is how you fill the space.</p>
            {habits.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {habits.map((h, idx) => (
                  <div key={idx} className="rounded-3xl bg-card border border-border p-6 shadow-soft" data-testid={`journey-habit-${idx}`}>
                    <p className="font-serif text-xl text-olive mb-2">{h.name}</p>
                    {h.cue && <p className="text-sm text-olive/70"><span className="text-stone">When</span> {h.cue}</p>}
                    {h.routine && <p className="text-sm text-olive/70"><span className="text-stone">I'll</span> {h.routine}</p>}
                    {h.reward && <p className="text-sm text-olive/70"><span className="text-stone">Reward</span> {h.reward}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <BuildStrategyPrompt testid="journey-habits-empty" line="Pick a few good habits to grow — a morning walk, more water, evening prayer — and we'll help them take root." />
            )}
          </div>

          <RoadmapCard to="/app/strategy" icon={Route} eyebrow="The first days" title="Your gentle first-days plan"
            desc="A 4-night, change-of-scene plan so you don't head home to old habits too soon — matched to your energy and what you love."
            testid="journey-firstdays" />
          <RoadmapCard to="/app/check-in" icon={HeartPulse} eyebrow="Daily rhythm" title="A gentle daily check-in"
            desc="Notice your mood, cravings, and gratitude each day. A steady rhythm is what makes the new self stick."
            testid="journey-checkin" />
        </div>
      );
    }
    if (i === 3) {
      return (
        <div className="space-y-4">
          <BreakChainButton variant="banner" />
          <RoadmapCard to="/app/toolkit" icon={LifeBuoy} eyebrow="Your toolkit" title="Tools for any craving, any day"
            desc="Guided breathing, an urge timer, grounding, and prayer — ride the wave; it always passes."
            testid="journey-toolkit" accent="gold" />
          <p className="flex items-start gap-2 text-sm text-stone">
            <Shield className="w-4 h-4 text-brand-teal-dark mt-0.5 shrink-0" />
            Break the Chain is always here — from any screen, any day. You don't have to white-knuckle it.
          </p>
        </div>
      );
    }
    // i === 4
    return (
      <div className="space-y-4">
        <RoadmapCard to="/app/together" icon={HandHeart} eyebrow="Community" title="Ask for prayer, or pray for others"
          desc="You don't walk this alone. Be prayed for when it's heavy, and lift someone else up when you can."
          testid="journey-together" accent="gold" />
        <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="journey-milestones">
          <h3 className="font-serif text-2xl text-olive mb-1">Healing milestones</h3>
          <p className="text-sm text-stone mb-5">What your body reclaims along the way — {sub?.label?.toLowerCase()}.</p>
          <div className="space-y-3">
            {milestones.map((m) => {
              const reached = data.hours_since_quit >= m.hours;
              return (
                <div key={m.title} className="flex items-start gap-3" data-testid={`journey-milestone-${m.title.replace(/\s/g, "")}`}>
                  {reached ? <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-stone/40 shrink-0 mt-0.5" />}
                  <div>
                    <p className={`font-medium ${reached ? "text-olive" : "text-stone"} flex items-center gap-1.5`}>
                      {reached && <Flag className="w-3.5 h-3.5 text-gold" />}{m.title}
                    </p>
                    <p className="text-sm text-olive/60">{m.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="rounded-2xl bg-sage/60 p-5">
          <p className="text-olive/80 leading-relaxed">A slip is information, not failure. His mercies are new every morning — you begin again in grace, not shame.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="journey-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-brand-teal-dark mb-2">The road to freedom</p>
        <h1 className="font-serif text-display text-olive">My Journey</h1>
        <p className="text-olive/70 mt-3 max-w-2xl">
          One step at a time. This isn't about white-knuckling through — it's about renewing your mind and building a new life
          that leaves no room for the old one. Here's where you are, and the next thing to lean into.
        </p>
      </div>

      {/* Where you are */}
      <div className="rounded-3xl bg-brand-navy-light text-brand-ivory border border-brand-teal/20 p-5 sm:p-7 shadow-soft" data-testid="journey-status">
        <p className="uppercase tracking-[0.2em] text-[11px] text-gold mb-3">Where you are</p>
        <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
          <div>
            <p className="font-serif text-3xl sm:text-4xl leading-none">
              {daysClean > 0 ? daysClean : Math.max(0, Math.ceil((new Date(active.quit_date) - new Date()) / 86400000))}
            </p>
            <p className="text-brand-ivory-muted text-sm mt-1.5">{daysClean > 0 ? "days reclaimed" : "days to your quit day"}</p>
          </div>
          <div>
            <p className="font-serif text-3xl sm:text-4xl leading-none">{data.chain_breaks ?? 0}</p>
            <p className="text-brand-ivory-muted text-sm mt-1.5">chain breaks so far</p>
          </div>
          <p className="hidden sm:block font-serif italic text-brand-ivory-muted flex-1 min-w-[240px]">
            “Being confident of this, that He who began a good work in you will carry it on to completion.” — Philippians 1:6
          </p>
        </div>
      </div>

      {/* Guided stages */}
      <div className="space-y-3" data-testid="journey-path-guided">
        {STAGES.map((st, i) => {
          const Icon = st.icon;
          const done = doneMap[i];
          const isOpen = shownOpen === i;
          const isCurrent = i === activeStage;
          return (
            <div key={st.key} data-testid={`journey-stage-${i}`}
              className={`rounded-3xl border shadow-soft overflow-hidden ${isCurrent ? "border-brand-teal/50" : "border-border"} bg-card`}>
              <button data-testid={`stage-toggle-${i}`} onClick={() => setOpenIndex(isOpen ? -1 : i)}
                className="w-full flex items-center gap-4 p-5 text-left">
                <div className={`rounded-2xl w-11 h-11 flex items-center justify-center shrink-0 ${done ? "bg-brand-teal-dark text-white" : isCurrent ? "bg-brand-teal/15 text-brand-teal-dark" : "bg-sage text-stone"}`}>
                  {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[11px] uppercase tracking-[0.15em] text-stone">{st.eyebrow}</p>
                    {isCurrent && (
                      <span data-testid={`stage-current-${i}`} className="rounded-full bg-brand-teal/15 text-brand-teal-dark text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                        You're here
                      </span>
                    )}
                  </div>
                  <h2 className="font-serif text-2xl text-olive leading-tight">{st.title}</h2>
                </div>
                <ChevronDown className={`w-5 h-5 text-stone shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div key="content" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
                    <div className="px-5 pb-6">
                      <div className="flex items-start gap-2.5 rounded-2xl bg-sage/50 p-4 mb-5">
                        <Icon className="w-4 h-4 text-brand-teal-dark mt-0.5 shrink-0" />
                        <p className="text-sm text-olive/80 leading-relaxed">{st.why}</p>
                      </div>
                      {renderStageContent(i)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <ScriptureCard />
    </div>
  );
}
